/**
 * CollateralVault ABI - Actual Anvil Protocol Implementation
 * 
 * IMPORTANT: Anvil uses account-based collateral tracking, NOT vault IDs.
 * There is NO separate "createVault" function - the first deposit creates the account.
 * 
 * Source: https://github.com/AcronymFoundation/anvil-contracts/blob/main/contracts/CollateralVault.sol
 * Deployed: https://etherscan.io/address/0x5d2725fdE4d7Aa3388DA4519ac0449Cc031d675f
 */

export const CollateralVaultABI = [
  // View Functions
  {
    inputs: [
      { name: '_accountAddress', type: 'address' },
      { name: '_tokenAddress', type: 'address' },
    ],
    name: 'getAccountCollateralBalance',
    outputs: [
      { name: 'availableBalance', type: 'uint256' },
      { name: 'reservedBalance', type: 'uint256' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: '_accountAddress', type: 'address' },
      { name: '_collateralizableContractAddress', type: 'address' },
      { name: '_tokenAddress', type: 'address' },
    ],
    name: 'getCollateralizableTokenAllowance',
    outputs: [{ name: 'allowance', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: '_tokenAddress', type: 'address' }],
    name: 'getCollateralToken',
    outputs: [
      { name: 'enabled', type: 'bool' },
      { name: 'decimals', type: 'uint8' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getWithdrawalFeeBasisPoints',
    outputs: [{ name: 'feeBasisPoints', type: 'uint16' }],
    stateMutability: 'view',
    type: 'function',
  },

  // Write Functions
  {
    inputs: [
      { name: '_accountAddress', type: 'address' },
      { name: '_tokenAddresses', type: 'address[]' },
      { name: '_amounts', type: 'uint256[]' },
    ],
    name: 'depositToAccount',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: '_tokenAddresses', type: 'address[]' },
      { name: '_amounts', type: 'uint256[]' },
      { name: '_collateralizableContractAddressToApprove', type: 'address' },
    ],
    name: 'depositAndApprove',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: '_tokenAddress', type: 'address' },
      { name: '_amount', type: 'uint256' },
      { name: '_destinationAddress', type: 'address' },
    ],
    name: 'withdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: '_collateralizableContractAddress', type: 'address' },
      { name: '_tokenAddress', type: 'address' },
      { name: '_byAmount', type: 'int256' },
    ],
    name: 'modifyCollateralizableTokenAllowance',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },

  // Events
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: 'depositor', type: 'address' },
      { indexed: true, name: 'accountAddress', type: 'address' },
      { indexed: true, name: 'tokenAddress', type: 'address' },
      { indexed: false, name: 'amount', type: 'uint256' },
    ],
    name: 'Deposit',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: 'accountAddress', type: 'address' },
      { indexed: true, name: 'tokenAddress', type: 'address' },
      { indexed: false, name: 'amount', type: 'uint256' },
      { indexed: true, name: 'destinationAddress', type: 'address' },
    ],
    name: 'Withdrawal',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: 'accountAddress', type: 'address' },
      { indexed: true, name: 'tokenAddress', type: 'address' },
      { indexed: true, name: 'collateralizableContractAddress', type: 'address' },
      { indexed: false, name: 'allowance', type: 'uint256' },
    ],
    name: 'CollateralizableTokenAllowanceModified',
    type: 'event',
  },
] as const

